import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/apiFetch';
import toast from 'react-hot-toast';

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [syncInterval, setSyncInterval] = useState(30000); // 30 saniye

  // Offline veri yönetimi
  const saveOfflineData = useCallback((key, data) => {
    try {
      const offlineData = JSON.parse(localStorage.getItem('offlineData') || '{}');
      offlineData[key] = {
        data,
        timestamp: Date.now(),
        version: Date.now() // Basit versiyon kontrolü
      };
      localStorage.setItem('offlineData', JSON.stringify(offlineData));
      return true;
    } catch (error) {
      console.error('Offline veri kaydedilemedi:', error);
      return false;
    }
  }, []);

  const getOfflineData = useCallback((key) => {
    try {
      const offlineData = JSON.parse(localStorage.getItem('offlineData') || '{}');
      return offlineData[key]?.data || null;
    } catch (error) {
      console.error('Offline veri alınamadı:', error);
      return null;
    }
  }, []);

  const setOfflineData = useCallback((key, data) => {
    return saveOfflineData(key, data);
  }, [saveOfflineData]);

  // Bekleyen işlemleri yönet
  const addPendingAction = useCallback((action) => {
    const newAction = {
      id: Date.now() + Math.random(),
      ...action,
      timestamp: Date.now(),
      retryCount: 0
    };
    setPendingActions(prev => [...prev, newAction]);
    
    // LocalStorage'a kaydet
    const stored = JSON.parse(localStorage.getItem('pendingActions') || '[]');
    stored.push(newAction);
    localStorage.setItem('pendingActions', JSON.stringify(stored));
    
    toast.success('İşlem offline olarak kaydedildi');
  }, []);

  const removePendingAction = useCallback((actionId) => {
    setPendingActions(prev => prev.filter(action => action.id !== actionId));
    
    // LocalStorage'dan kaldır
    const stored = JSON.parse(localStorage.getItem('pendingActions') || '[]');
    const filtered = stored.filter(action => action.id !== actionId);
    localStorage.setItem('pendingActions', JSON.stringify(filtered));
  }, []);

  // Senkronizasyon işlemi
  const syncPendingActions = useCallback(async () => {
    if (!isOnline || syncing || pendingActions.length === 0) return;

    setSyncing(true);
    const errors = [];
    const successful = [];

    for (const action of pendingActions) {
      try {
        // Maksimum 3 deneme
        if (action.retryCount >= 3) {
          errors.push({
            action,
            error: 'Maksimum deneme sayısı aşıldı'
          });
          continue;
        }

        // API çağrısını yap.
        //
        // apiFetch kullaniyoruz: kuyruga alinan istek saatler once
        // olusturulmus olabilir ve action.headers icindeki token coktan
        // dolmustur. apiFetch, axios interceptor'i uzerinden HER ZAMAN guncel
        // token'i ekler ve gerekirse yeniler.
        const response = await apiFetch(action.url, {
          method: action.method,
          headers: {
            'Content-Type': 'application/json',
            ...action.headers
          },
          body: action.method !== 'GET' ? JSON.stringify(action.data) : undefined
        });

        if (response.ok) {
          successful.push(action);
          removePendingAction(action.id);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.error('Senkronizasyon hatası:', error);
        
        // Retry count'u artır
        const updatedAction = { ...action, retryCount: action.retryCount + 1 };
        setPendingActions(prev => 
          prev.map(a => a.id === action.id ? updatedAction : a)
        );
        
        errors.push({ action, error: error.message });
      }
    }

    setSyncErrors(errors);
    setLastSyncTime(Date.now());

    if (successful.length > 0) {
      toast.success(`${successful.length} işlem başarıyla senkronize edildi`);
    }

    if (errors.length > 0) {
      toast.error(`${errors.length} işlem senkronize edilemedi`);
    }

    setSyncing(false);
  }, [isOnline, syncing, pendingActions, removePendingAction]);

  // Manuel senkronizasyon
  const manualSync = useCallback(() => {
    if (!isOnline) {
      toast.error('İnternet bağlantısı yok');
      return;
    }
    syncPendingActions();
  }, [isOnline, syncPendingActions]);

  // Bekleyen işlem sayısını al
  const getPendingCount = useCallback(() => {
    return pendingActions.length;
  }, [pendingActions]);

  // Offline verileri temizle
  const clearOfflineData = useCallback(() => {
    localStorage.removeItem('offlineData');
    localStorage.removeItem('pendingActions');
    setPendingActions([]);
    setSyncErrors([]);
    toast.success('Offline veriler temizlendi');
  }, []);

  // Çakışma çözümü
  const resolveConflict = useCallback((localData, serverData, strategy = 'server') => {
    switch (strategy) {
      case 'server':
        return serverData;
      case 'local':
        return localData;
      case 'merge':
        return { ...localData, ...serverData };
      case 'timestamp':
        return localData.timestamp > serverData.timestamp ? localData : serverData;
      default:
        return serverData;
    }
  }, []);

  // Otomatik senkronizasyon
  useEffect(() => {
    if (!autoSyncEnabled || !isOnline) return;

    const interval = setInterval(() => {
      if (pendingActions.length > 0) {
        syncPendingActions();
      }
    }, syncInterval);

    return () => clearInterval(interval);
  }, [autoSyncEnabled, isOnline, pendingActions.length, syncInterval, syncPendingActions]);

  // Network durumu değişikliklerini dinle
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('İnternet bağlantısı kuruldu');
      
      // Online olduğunda otomatik senkronizasyon
      if (pendingActions.length > 0) {
        setTimeout(() => syncPendingActions(), 1000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error('İnternet bağlantısı kesildi - Offline mod aktif');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pendingActions.length, syncPendingActions]);

  // LocalStorage'dan bekleyen işlemleri yükle
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('pendingActions') || '[]');
      setPendingActions(stored);
    } catch (error) {
      console.error('Bekleyen işlemler yüklenemedi:', error);
    }
  }, []);

  const value = {
    isOnline,
    pendingActions,
    syncing,
    lastSyncTime,
    syncErrors,
    autoSyncEnabled,
    syncInterval,
    addPendingAction,
    removePendingAction,
    syncPendingActions,
    manualSync,
    getPendingCount,
    clearOfflineData,
    saveOfflineData,
    getOfflineData,
    setOfflineData,
    resolveConflict,
    setAutoSyncEnabled,
    setSyncInterval
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

